import { useState } from "react";
import { useSelector } from "react-redux";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import {FormattedMessage } from "react-intl";

import { formatPrice } from "../../../util/format";
import { SubscriptionButton } from "..";

// Constants
import { GA_EVENTS } from "../../../constants/analytics"
import { PLAN_TYPES, TOKEN_PRICE, TOKENS_AMOUNT_TO_ADD } from "../../../constants";

const DividerLine = ({ text = <FormattedMessage id="common.or" /> }) => {
  return (
    <div className="option-divider">
      <div className="option-divider-line"></div>
      <div className="option-divider-text">{text}</div>
      <div className="option-divider-line"></div>
    </div>
  );
};

const getMinTokens = (email) => {
  if (email && email.endsWith('@fribl.co')) {
    return TOKENS_AMOUNT_TO_ADD.MIN_FRIBL_USERS;
  }
  return TOKENS_AMOUNT_TO_ADD.MIN;
};

const ChoosePricingSection = ({
  handleContinueClick,
  loading,
  planType,
  topOffset = 0,
}) => {
  const tokenCosts = useSelector((state) => state.config.tokenCosts)
  const email = useSelector((state) => state.auth.user.email);
  const selectedProduct = useSelector((state) => state.signup.product);

  const tokenPrice = parseFloat(selectedProduct?.pricePerToken || TOKEN_PRICE);
  const isEnterprise = planType === PLAN_TYPES.ENTERPRISE;

  const minTokens = getMinTokens(email);  
  const [tokens, setTokens] = useState(minTokens);

  const handleTokensChange = ({target}) => {
    const {value} = target;
    setTokens(value);
  }

  // Restrict the input to the min value if the user tries to input a lower value
  const handleTokensBlur = ({target}) => {
    const {value} = target;
    setTokens(value < minTokens ? minTokens : value);
  }

  const planDescription = isEnterprise ? (
    <>
      <p><b><FormattedMessage id='choosePricing.planDescription.noHiresPlan' /></b></p>
      <p><FormattedMessage id='choosePricing.planDescription.talkToUs' /></p>
    </>
  ) : (
    <>
      <p>
        <FormattedMessage id='choosePricing.planDescription.cvs1'/>
        <b>
          {' '}{Math.floor(tokens / tokenCosts.CV_ANALYSIS)}{' '}
          <FormattedMessage id='choosePricing.planDescription.cvs2' />
        </b>
        <br/>
        <span className="small">
          {"* "}<FormattedMessage id='choosePricing.planDescription.cvsDetail' values={{ cvCost: tokenCosts.CV_ANALYSIS }}/>
        </span>
      </p>

      <DividerLine />

      <p>
        <FormattedMessage id='choosePricing.planDescription.jobs1'/>
        <b>
          {' '}{Math.floor(tokens / tokenCosts.JOB_ANALYSIS)}{' '}
          <FormattedMessage id='choosePricing.planDescription.jobs2' />
        </b>
        <br/>
        <span className="small">
          {"* "}<FormattedMessage id='choosePricing.planDescription.jobsDetail' values={{ jobCost: tokenCosts.JOB_ANALYSIS }}/>
        </span>
      </p>

      <DividerLine />

      <p>
        <FormattedMessage id='choosePricing.planDescription.matches1' />
        <b>
          {' '}{Math.floor(tokens / tokenCosts.JOB_MATCHING)}{' '}
          <FormattedMessage id='choosePricing.planDescription.matches2' />
        </b>
        <br/>
        <span className="small">
          {"* "}<FormattedMessage id='choosePricing.planDescription.matchesDetail' values={{ matchCost: tokenCosts.JOB_MATCHING }}/>
        </span>
      </p>

      <DividerLine />

      <p className="normalCentered" >
        <b><FormattedMessage id='choosePricing.planDescription.anyCombination' /></b>
      </p>
    </>
  );

  return (
    <section id="choose-pricing-section" >
      <div id="choose-pricing-container" style={{ top: topOffset }}>

        {!isEnterprise && (
          <>
            <div id="choose-pricing-container-header">
              <p className="h3"><FormattedMessage id='choosePricing.header.title1' /></p>
              <input
                type="number"
                placeholder={minTokens}
                value={tokens}
                onChange={handleTokensChange}
                onBlur={handleTokensBlur}
              />
              <p className="h3"><FormattedMessage id='choosePricing.header.title2' /></p>
              <p><b><FormattedMessage id='choosePricing.header.subtitle' /></b></p>
            </div>

            <div id="tokens-slider">
              <Slider
                onChange={(nextValues) => setTokens(nextValues)}
                onChangeComplete={(value) => setTokens(value)}
                min={minTokens}
                max={TOKENS_AMOUNT_TO_ADD.MAX}
                value={tokens}
                step={1}
              />
            </div>
          </>
        )}

        <div id="plan-description">
          {planDescription}
          
          {!isEnterprise && (
            <p className="green">
              <b>{formatPrice(tokens * tokenPrice)}{' €'}</b>{' '}
              <span className="normal">
                <FormattedMessage id='choosePricing.priceMessage.oneOff1' />
              </span>
            </p>
          )}

          {isEnterprise && (
            <p className="normal">
              <b><FormattedMessage id='choosePricing.planDescription.enterpriseDesc1' /></b>
            </p>
          )}

          <SubscriptionButton
            className="primary btn"
            disabled={!isEnterprise && tokens < minTokens}
            isPopular={!isEnterprise}
            onClick={() => handleContinueClick(tokens)}
            loading={loading}
            text={
              <FormattedMessage
                id={`choosePricing.button.${isEnterprise ? 'getAQuote' : 'continue'}`}
              />
            }
            trackingEvent={{
              ...GA_EVENTS.CHOOSE_PRICING.BILLING_SELECTED,
              label: `${planType}: ${tokens} Tokens`,
            }}
          />

          {!isEnterprise && (
            <p className="small">
              <FormattedMessage id='choosePricing.proceedToPayment.payment1' />{' '}
              <b><FormattedMessage id='choosePricing.proceedToPayment.payment2' /></b>{' '}
              <FormattedMessage id='choosePricing.proceedToPayment.payment3' />
            </p>
          )}

        <p className="small">
          <b><FormattedMessage id='choosePricing.proceedToPayment.reminder' /></b>{' '}
          <FormattedMessage id='choosePricing.proceedToPayment.tokenExpiration' />
        </p>

          {isEnterprise && (
            <p className="normal">
              <FormattedMessage id='choosePricing.planDescription.enterpriseDesc3' />
            </p>
          )}
        </div>

        {isEnterprise && (
          <p className="green normal">
            <b><FormattedMessage id='choosePricing.billing.enterprise1' /></b>
            {' '}<FormattedMessage id='choosePricing.billing.enterprise2' />
          </p>
        )}
      </div>
    </section>
  );
}

export default ChoosePricingSection;